import {commaSeparate, getCurrentGame, getRandomInt, objectInArray} from "./helpers.js";
import {Superstars, Companies, CompanyLogs, Relationships, TradeOffers, TalentRequests, db} from "../models/models.js";
import {getGameCurrentUserCompany, getGameMainUserCompany} from "./games.js";
import {
    getCompanyMainRosterSuperstars,
    getCompanyOwnerName,
    getCompanyRosterByType,
    getCompanyUnreadMessages, addFormerCompanyRelationship, getRandomStaffMemberByRole, getCompanyLatestEvent
} from "./companies.js";
import {
    addCompanyCompanyLogs,
    addCompanyMessages, addCompanyNewsItems,
    addCompanyReceivedTradeOffers,
    addCompanySentTradeOffers,
    getCompanyChampionships,
    getCompanyRandomEvents, getCompanyReceivedTradeOffers, getCompanyStaff
} from "../models/company.js";
import {
    addTradeOfferOfferedSuperstars,
    addTradeOfferRequestedSuperstars,
    getTradeOfferOfferedSuperstars,
    getTradeOfferReceiverCompany,
    getTradeOfferRequestedSuperstars,
    getTradeOfferSenderCompany, removeTradeOfferOfferedSuperstars, removeTradeOfferRequestedSuperstars,
    TradeOffer
} from "../models/tradeOffer.js";
import {
    changeSuperstarMoraleToHappy,
    getRankFromPopularity, removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships
} from "./superstars.js";
import {createMessage} from "./messages.js";
import {CompanyLog} from "../models/companyLog.js";
import {addRosterOriginalSuperstars, addRosterSuperstars, addRosterTagTeams} from "../models/roster.js";
import {
    getSuperstarFeuds,
    getSuperstarRelationships,
    getSuperstarTagTeams, getSuperstarTalentRequests
} from "../models/superstar.js";
import {endFeud} from "./feuds.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {removeTagTeam} from "./tagTeams.js";
import {generateNewsItem} from "./news.js";
import {getSuperstarsBookedAtEventMatches} from "./matches.js";
import {processPowSuperstarLeavesCompany} from "./passionOfWrestling.js";

async function getTradeAvailableSuperstars(companyUid) {  // get_trade_offer_available_superstars
    let data = {};
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get superstars, sort
    const mainRosterSuperstars = await getCompanyMainRosterSuperstars(selectedCompany);
    const sortedSuperstarsByPopularity = [...mainRosterSuperstars].sort((a, b) => b.popularity - a.popularity);

    // Get championships' superstar ids, including stolen title champions
    let championshipsSuperstarsIds = (await getCompanyChampionships(selectedCompany)).map(x => x.superstarId);
    let championshipStolenSuperstarsIds = (await getCompanyRandomEvents(selectedCompany))
        .filter(x => x.name === 'championshipStolen' && x.monthsRemaining > 0)
        .map(y => y.superstarId);
    championshipsSuperstarsIds.push(...championshipStolenSuperstarsIds);

    const latestEvent = await getCompanyLatestEvent(selectedCompany);
    const bookedSuperstars = await getSuperstarsBookedAtEventMatches(latestEvent);

    data['availableSuperstars'] = sortedSuperstarsByPopularity.map(x => {
        return {
            'name': x.name,
            'popularity': x.popularity,
            'uid': x.$loki,
            'retired': x.retired,
            'booked': objectInArray(x, bookedSuperstars),
            'contractMonthsRemaining': x.contractMonthsRemaining,
            'fullTimeManager': x.fullTimeManager,
            'appearances': x.partTimer && !x.retired ? x.appearances : null,
            'injured': x.injured > 0 ? x.injured : null,
            'onTradeDeal': x.activeEffect === 'traded',
            'isChampion': championshipsSuperstarsIds.indexOf(x.$loki) >= 0,
            'partOfTradeOffer': !!(x.offeredTradeOfferId || x.requestedTradeOfferId),
            'isOwner': x.name === selectedCompany.ownerName,
            'powStar': selectedCompany.storyName === 'passion' &&
                ['Lady Julia', 'Violet', 'Natasha Ali', 'Zora'].indexOf(x.name) >= 0
        }
    });

    return data;
}

async function makeTradeOffer(offeredSuperstarsUids, requestedSuperstarsUids, duration, companyUid) {  // make_trade_offer_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const cost = 100;

    // Get competitor
    let competitor = await Companies.get(companyUid);
    if (!competitor) {
        data['error'] = 'competitor-not-found';
        return data;
    }

    // Check points
    if (selectedCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check mars story
    if (selectedCompany.storyName === 'mars') {
        data['error'] = 'mars';
        return data;
    }

    // Get superstars, remove empty values
    let offeredSuperstars = await Promise.all(offeredSuperstarsUids.map(x => Superstars.get(x)));
    let requestedSuperstars = await Promise.all(requestedSuperstarsUids.map(x => Superstars.get(x)));
    offeredSuperstars = offeredSuperstars.filter(x => x);
    requestedSuperstars = requestedSuperstars.filter(x => x);

    // Check if superstars present
    if (offeredSuperstars.length <= 0 || requestedSuperstars.length <= 0) {
        data['error'] = 'superstars-not-found';
        return data;
    }

    // Create trade offer
    const tradeOffer = await createTradeOffer(selectedCompany, competitor, offeredSuperstars, requestedSuperstars, duration);

    // Process trade offer reply if not sent to user controlled company
    if (!competitor.userControlled) {
        await processCpuReplyToTradeOffer(tradeOffer)
    }

    // Remove points
    selectedCompany.points -= cost;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;
    data['competitorName'] = competitor.name;
    const unreadMessages = await getCompanyUnreadMessages(selectedCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function createTradeOffer(senderCompany, receiverCompany, offeredSuperstars, requestedSuperstars,
                                duration = null) {  // make_trade_offer
    const tradeOffer = TradeOffer();
    if (duration) {
        tradeOffer.duration = duration
    }
    await TradeOffers.insert(tradeOffer);

    // Add trade offer superstars and companies
    await addTradeOfferOfferedSuperstars(tradeOffer, offeredSuperstars);
    await addTradeOfferRequestedSuperstars(tradeOffer, requestedSuperstars);
    await addCompanySentTradeOffers(senderCompany, [tradeOffer]);
    await addCompanyReceivedTradeOffers(receiverCompany, [tradeOffer]);

    return tradeOffer;
}

async function processCpuReplyToTradeOffer(tradeOffer) {  // processCpuReplyToTradeOffer
    const acceptChance = await getTradeOfferAcceptChance(tradeOffer);
    if (getRandomInt(0, 100) <= acceptChance) {
        await acceptTradeOffer(tradeOffer)
    }
    else {
        await declineTradeOffer(tradeOffer)
    }
}

async function getTradeOfferAcceptChance(tradeOffer) {  // get_trade_offer_accept_chance
    const offeredSuperstars = await getTradeOfferOfferedSuperstars(tradeOffer);
    const requestedSuperstars = await getTradeOfferRequestedSuperstars(tradeOffer);

    const offeredSuperstarsPoints = getTradeOfferSuperstarsPoints(offeredSuperstars);
    const requestedSuperstarsPoints = getTradeOfferSuperstarsPoints(requestedSuperstars);
    const pointsDifference = offeredSuperstarsPoints - requestedSuperstarsPoints;

    let acceptChance;
    if (pointsDifference >= 100) {
        acceptChance = 100
    }
    else if (pointsDifference >= -50) {
        acceptChance = 80
    }
    else if (pointsDifference >= -100) {
        acceptChance = 40
    }
    else {
        acceptChance = -1
    }

    return acceptChance;
}

function getTradeOfferSuperstarsPoints(superstars) {  // get_trade_offer_superstars_points
    let points = 0;
    for (const x of superstars) {
        let p = 0;
        p += x.popularity;
        if (getRankFromPopularity(x.popularity) === 'Main Eventer' || x.faceOfTheCompany || x.hot) {
            p += 200
        }
        if (x.stale || x.retired || x.partTimer || x.injured > 0 || x.fullTimeManager) {
            p -= 200
        }
        if ((x.partTimer && x.appearances <= 0) || x.retired) {
            p /= 2
        }
        points += p;
    }
    points /= superstars.length;
    points += points * 0.125 * (superstars.length - 1);
    return points;
}

async function acceptTradeOffer(tradeOffer) {
    const currentGame = await getCurrentGame();
    const mainCompany = await getGameMainUserCompany(currentGame);
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const senderCompany = await getTradeOfferSenderCompany(tradeOffer);
    const senderCompanyMainRoster = await getCompanyRosterByType(senderCompany, 'main');
    const receiverCompany = await getTradeOfferReceiverCompany(tradeOffer);
    const receiverCompanyMainRoster = await getCompanyRosterByType(receiverCompany, 'main');
    const offeredSuperstars = await getTradeOfferOfferedSuperstars(tradeOffer);
    const requestedSuperstars = await getTradeOfferRequestedSuperstars(tradeOffer);
    const tradeOfferSuperstars = offeredSuperstars.concat(requestedSuperstars);
    const requestedSuperstarsNames = commaSeparate(requestedSuperstars.map(x => x.name));
    const offeredSuperstarsNames = commaSeparate(offeredSuperstars.map(x => x.name));
    const offeredMovedTeams = [];
    const requestedMovedTeams = [];

    // Transfer superstars
    for (const x of tradeOfferSuperstars) {

        // Get roster to transfer to
        let superstarRoster;
        let rosterToSendTo;
        if (objectInArray(x, offeredSuperstars)) {
            superstarRoster = senderCompanyMainRoster;
            rosterToSendTo = receiverCompanyMainRoster;
        }
        else {
            superstarRoster = receiverCompanyMainRoster;
            rosterToSendTo = senderCompanyMainRoster;
        }

        // Transfer to new roster
        await addRosterSuperstars(rosterToSendTo, [x]);

        // Move teams if all members present in trade offer
        const superstarTagTeams = (await getSuperstarTagTeams(x)).filter(y => !y.temporary);
        for (const tagTeam of superstarTagTeams) {
            const tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
            if (tagTeamSuperstars.every(y => objectInArray(y, tradeOfferSuperstars))) {
                await addRosterTagTeams(rosterToSendTo, [tagTeam]);
                if (objectInArray(x, offeredSuperstars) && !objectInArray(tagTeam, offeredMovedTeams)) {
                    offeredMovedTeams.push(tagTeam)
                }
                if (objectInArray(x, requestedSuperstars) && !objectInArray(tagTeam, requestedMovedTeams)) {
                    requestedMovedTeams.push(tagTeam)
                }
            }
            else {
                await removeTagTeam(tagTeam)
            }
        }

        // Add temp trade attributes
        if (tradeOffer.duration) {
            x.activeEffect = 'traded';
            x.appearances = tradeOffer.duration;
            // Add original roster
            await addRosterOriginalSuperstars(superstarRoster, [x]);
        }
        // Process permanent trade
        else {
            // End feuds
            for (const feud of (await getSuperstarFeuds(x))) {
                if (!feud.ended) {
                     await endFeud(feud, currentGame.monthCount, currentGame.year);
                }
            }

            // Make superstar happy
            changeSuperstarMoraleToHappy(x);

            // Delete talent requests
            for (const talentRequest of (await getSuperstarTalentRequests(x))) {
                await TalentRequests.remove(talentRequest)
            }

            // Delete all relationships, add former company relationship
            for (const relationship of (await getSuperstarRelationships(x))) {
                await Relationships.remove(relationship)
            }
            if (objectInArray(x, offeredSuperstars)) {
                await addFormerCompanyRelationship(x, senderCompany)
            }
            else {
                await addFormerCompanyRelationship(x, receiverCompany)
            }

            // Process POW stars leaving
            if (userCompany.storyName === 'passion') {
                await processPowSuperstarLeavesCompany(x, userCompany)
            }
        }

        // Remove superstar from factions and managers
        await removeSuperstarFactionRelationships(x, currentGame.monthCount, currentGame.year);
        await removeSuperstarManagerRelationships(x);

        // Reset attributes
        x.brandName = null;
        x.faceOfTheCompanyCounter = 0;
        x.suspensionMonths = 0;
    }

    // Add message from receiver company to sender company
    if (senderCompany.userControlled) {
        const subject = 'Trade accepted';
        let durationText = '';
        if (tradeOffer.duration) {
            let monthsWord = tradeOffer.duration === 1 ? 'month' : 'months';
            durationText = ` for the next ${tradeOffer.duration} ${monthsWord}`;
        }
        const content = `<p>We have accepted your offer to trade ${requestedSuperstarsNames}` +
            ` for ${offeredSuperstarsNames}. We hope you take good care of our ` +
            `talent${durationText} and we'll do the same with yours.</p>`;
        const messageFrom = getCompanyOwnerName(receiverCompany);
        const newMessage = await createMessage(subject, messageFrom, currentGame.monthCount, currentGame.year, content);
        await addCompanyMessages(senderCompany, [newMessage]);
    }

    // Add message from talent manager of receiver company
    if (receiverCompany.userControlled) {
        const subject = 'Trade deal';
        let durationText = '';
        let verb = offeredSuperstars.length === 1 ? 'has' : 'have';
        if (tradeOffer.duration) {
            let monthsWord = tradeOffer.duration === 1 ? 'month' : 'months';
            durationText = ` for the next ${tradeOffer.duration} ${monthsWord}`;
        }
        const content = `<p>The deal to trade ${requestedSuperstarsNames} for ${offeredSuperstarsNames}${durationText} ` +
            `was accepted. ${offeredSuperstarsNames} ${verb} joined our roster.</p>`;
        const talentManager = getRandomStaffMemberByRole((await getCompanyStaff(receiverCompany)), 'talent_manager');
        const messageFrom = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, messageFrom, currentGame.monthCount, currentGame.year, content);
        await addCompanyMessages(receiverCompany, [newMessage]);
    }

    // Add company log to both companies
    for (const company of [senderCompany, receiverCompany]) {
        const newCompanyLog = CompanyLog(currentGame.monthCount, userCompany.realYear);
        await CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = `${offeredSuperstarsNames} for ${requestedSuperstarsNames}`;
        newCompanyLog.value = tradeOffer.duration;
        newCompanyLog.type = 'trade-deal';
        newCompanyLog.realYear = currentGame.year;
        await addCompanyCompanyLogs(company, [newCompanyLog]);
    }

    // Add new trade superstar logs for superstars and teams moved
    for (const x of tradeOfferSuperstars.concat(offeredMovedTeams, requestedMovedTeams)) {
        const newCompanyLog = CompanyLog(currentGame.monthCount, userCompany.realYear);
        await CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = x.name;
        newCompanyLog.type = 'new-trade-superstar';
        newCompanyLog.realYear = currentGame.year;
        if (objectInArray(x, offeredSuperstars) || objectInArray(x, offeredMovedTeams)) {
            await addCompanyCompanyLogs(receiverCompany, [newCompanyLog]);
        }
        if (objectInArray(x, requestedSuperstars) || objectInArray(x, requestedMovedTeams)) {
            await addCompanyCompanyLogs(senderCompany, [newCompanyLog]);
        }
    }

    // Add news
    let newsItem = await generateNewsItem({company: senderCompany, month: currentGame.monthCount, year: currentGame.year,
        competitor: receiverCompany, newsType: 'trade_deal'});
    await addCompanyNewsItems(mainCompany, [newsItem]);

    // Delete trade offer
    await removeTradeOfferRequestedSuperstars(tradeOffer, requestedSuperstars);
    await removeTradeOfferOfferedSuperstars(tradeOffer, offeredSuperstars);
    await TradeOffers.remove(tradeOffer);
}

async function declineTradeOffer(tradeOffer) {  // decline_trade_offer
    const senderCompany = await getTradeOfferSenderCompany(tradeOffer);
    const offeredSuperstars = await getTradeOfferOfferedSuperstars(tradeOffer);
    const requestedSuperstars = await getTradeOfferRequestedSuperstars(tradeOffer);

    // Add message from receiver company to sender company
    if (senderCompany.userControlled) {
        const receiverCompany = await getTradeOfferReceiverCompany(tradeOffer);
        const currentGame = await getCurrentGame();
        const subject = 'Trade declined';
        const content = `<p>We have rejected your offer to trade ${commaSeparate(requestedSuperstars.map(x => x.name))}` +
            ` for ${commaSeparate(offeredSuperstars.map(x => x.name))}. We don't think it will benefit us right now.</p>`;
        const messageFrom = getCompanyOwnerName(receiverCompany);
        const newMessage = await createMessage(subject, messageFrom, currentGame.monthCount, currentGame.year, content);
        await addCompanyMessages(senderCompany, [newMessage]);
    }

    // Delete trade offer
    await removeTradeOfferRequestedSuperstars(tradeOffer, requestedSuperstars);
    await removeTradeOfferOfferedSuperstars(tradeOffer, offeredSuperstars);
    await TradeOffers.remove(tradeOffer);
}

async function getTradeOfferData(tradeOffer) {  // get_trade_offer_data
    let data = {};
    const senderCompany = await getTradeOfferSenderCompany(tradeOffer);
    const receiverCompany = await getTradeOfferReceiverCompany(tradeOffer);
    const offeredSuperstars = await getTradeOfferOfferedSuperstars(tradeOffer);
    const requestedSuperstars = await getTradeOfferRequestedSuperstars(tradeOffer);

    data['offeredSuperstars'] = offeredSuperstars.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
    data['requestedSuperstars'] = requestedSuperstars.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
    data['senderCompany'] = senderCompany.name;
    data['receiverCompany'] = receiverCompany.name;
    data['duration'] = tradeOffer.duration;
    data['uid'] = tradeOffer.$loki;

    return data;
}

async function replyToTradeOfferRequest({tradeOfferUid, selectedChoice} = {}) {  // reply_to_trade_offer_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Get trade offer
    const tradeOffer = (await getCompanyReceivedTradeOffers(selectedCompany)).find(x => x.$loki === tradeOfferUid);
    if (!tradeOffer) {
        data['error'] = 'trade-offer-not-found';
        return data;
    }

    if (selectedChoice === 'accept') {
        await acceptTradeOffer(tradeOffer)
    }
    else {
        await declineTradeOffer(tradeOffer)
    }

    db.saveDatabase();

    // Get data to return
    const unreadMessages = await getCompanyUnreadMessages(selectedCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

export {getTradeAvailableSuperstars, makeTradeOffer, getTradeOfferData, replyToTradeOfferRequest}